// ─────────────────────────────────────────────────────────────────────────────
// HCEN · Super Admin · Support Tickets dashboard
//
// Hits /admin/tickets on HCEN_Admin_API (same gateway as the rest of the panel).
//
// Layout (May 17, 2026):
//   • Top: 6 summary cards (Open / In Progress / Resolved / Overdue / Resolved Today / Avg ★)
//   • Filters bar: status · category · priority · search
//   • Two-pane: left = ticket list (compact rows), right = ticket detail panel
//        - thread view with HCEN replies as admin bubbles
//        - status & priority editors
//        - reply box + internal notes textarea
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { adminTicketsAPI } from '../../services/api';

const STATUS = {
  open:        { bg: '#3a2e1e', fg: '#fbbf24', label: 'Open' },
  in_progress: { bg: '#1e3a5f', fg: '#93c5fd', label: 'In Progress' },
  resolved:    { bg: '#1e3b2c', fg: '#6ee7b7', label: 'Resolved' },
  closed:      { bg: '#374151', fg: '#9ca3af', label: 'Closed' },
};
const PRIORITY = {
  high:   { color: '#ef4444', label: '🔴 High' },
  medium: { color: '#fbbf24', label: '🟡 Medium' },
  low:    { color: '#6ee7b7', label: '🟢 Low' },
};
const CATEGORY = {
  payment_issue:   { emoji: '💳', label: 'Payment Issue' },
  refund:          { emoji: '💸', label: 'Refund / Dispute' },
  booking_issue:   { emoji: '📅', label: 'Booking Issue' },
  doctor_hospital: { emoji: '👨‍⚕️', label: 'Doctor/Hospital' },
  app_bug:         { emoji: '🐛', label: 'App Bug' },
  account:         { emoji: '👤', label: 'Account' },
  feedback:        { emoji: '💡', label: 'Feedback' },
  other:           { emoji: '❓', label: 'Other' },
};

export default function AdminTickets({ onBack, embedded = false }) {
  const [data, setData]       = useState({ tickets: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [filters, setFilters] = useState({ status: 'all', category: 'all', priority: 'all', q: '' });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (filters.status   !== 'all') params.status   = filters.status;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.priority !== 'all') params.priority = filters.priority;
      if (filters.q.trim())           params.q        = filters.q.trim();
      const r = await adminTicketsAPI.list(params);
      if (r.error) throw new Error(r.error);
      const payload = r.data || {};
      setData({
        tickets: Array.isArray(payload.tickets) ? payload.tickets : [],
        summary: payload.summary || null,
      });
    } catch (e) { setError(e.message); }
    finally    { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const tickets = Array.isArray(data?.tickets) ? data.tickets : [];
  const selected = useMemo(
    () => tickets.find(t => t.ticketId === selectedId) || null,
    [tickets, selectedId]
  );

  if (loading && !data.summary) return <div style={S.center}>Loading tickets…</div>;
  if (error) return (
    <div style={{ ...S.page, padding: 32 }}>
      <button style={S.backBtn} onClick={onBack}>← Back</button>
      <div style={S.errCard}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Could not load</div>
        <div style={{ fontSize: 13, color: '#fca5a5' }}>{error}</div>
        <button style={{ ...S.primaryBtn, marginTop: 16 }} onClick={load}>Retry</button>
      </div>
    </div>
  );

  return (
    <div style={{ ...S.page, ...(embedded ? { minHeight: 'auto', padding: 0, background: 'transparent' } : {}) }}>
      <div style={S.headerBar}>
        {!embedded && <button style={S.backBtn} onClick={onBack}>← Back</button>}
        <div style={S.title}>Support Tickets</div>
        <button style={S.refreshBtn} onClick={load}>↻ Refresh</button>
      </div>

      {/* Summary cards */}
      {data.summary && (
        <div style={S.grid}>
          <Stat label="Total"          value={data.summary.total}         accent="#93c5fd" />
          <Stat label="Open"           value={data.summary.open}          accent="#fbbf24" />
          <Stat label="In Progress"    value={data.summary.in_progress}   accent="#60a5fa" />
          <Stat label="Resolved Today" value={data.summary.resolvedToday} accent="#6ee7b7" />
          <Stat label="Overdue 🚨"     value={data.summary.overdue}       accent="#fca5a5" />
          <Stat label="Avg ★ Rating"   value={data.summary.avgRating ?? '—'} accent="#fbbf24" />
        </div>
      )}

      {/* Filters bar */}
      <div style={S.filterBar}>
        <Select value={filters.status} onChange={v => setFilters(f => ({ ...f, status: v }))}
          options={[['all','All status'],['open','Open'],['in_progress','In Progress'],['resolved','Resolved'],['closed','Closed']]} />
        <Select value={filters.category} onChange={v => setFilters(f => ({ ...f, category: v }))}
          options={[['all','All categories'], ...Object.entries(CATEGORY).map(([k,v]) => [k, v.emoji + ' ' + v.label])]} />
        <Select value={filters.priority} onChange={v => setFilters(f => ({ ...f, priority: v }))}
          options={[['all','All priorities'],['high','🔴 High'],['medium','🟡 Medium'],['low','🟢 Low']]} />
        <input
          style={S.search}
          placeholder="Search by ticket ID, subject, phone, name…"
          value={filters.q}
          onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
        />
      </div>

      {/* Two-pane layout */}
      <div className="resp-grid-sidebar-left" style={S.twoPane}>
        <div style={S.listPane}>
          {tickets.length === 0
            ? <div style={S.empty}>No tickets match your filters.</div>
            : tickets.map(t => (
              <TicketRow
                key={t.ticketId}
                ticket={t}
                selected={t.ticketId === selectedId}
                onClick={() => setSelectedId(t.ticketId)}
              />
            ))
          }
        </div>
        <div style={S.detailPane}>
          {selected
            ? <TicketDetail ticketId={selected.ticketId} onChange={load} />
            : <div style={{ ...S.empty, marginTop: 80 }}>← Pick a ticket to see the conversation</div>
          }
        </div>
      </div>
    </div>
  );
}

// ─── Ticket row ──────────────────────────────────────────────────────────────
function TicketRow({ ticket, selected, onClick }) {
  const s = STATUS[ticket.status]   || STATUS.open;
  const p = PRIORITY[ticket.priority] || PRIORITY.medium;
  const c = CATEGORY[ticket.category] || CATEGORY.other;
  const overdue = ticket.sla?.isOverdue && ticket.status !== 'resolved' && ticket.status !== 'closed';
  return (
    <div onClick={onClick} style={{
      ...S.row,
      background: selected ? '#1e3a5f' : '#1e293b',
      borderLeft: `3px solid ${selected ? '#60a5fa' : p.color}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 16 }}>{c.emoji}</span>
        <span style={{ flex: 1, fontWeight: 700, fontSize: 13, color: '#f9fafb',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ticket.subject}
        </span>
        <span style={{ background: s.bg, color: s.fg, padding: '2px 8px',
            borderRadius: 8, fontSize: 10, fontWeight: 700 }}>{s.label}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, fontSize: 10.5, color: '#94a3b8' }}>
        <span style={{ fontFamily: 'monospace', color: '#fbbf24' }}>{ticket.ticketId}</span>
        <span>·</span>
        <span>{ticket.userName} · {ticket.userPhone}</span>
        {overdue && <span style={{ color: '#fca5a5', fontWeight: 700, marginLeft: 'auto' }}>
          🚨 {ticket.sla.hoursOverdue}h overdue
        </span>}
      </div>
    </div>
  );
}

// ─── Detail pane ────────────────────────────────────────────────────────────
function TicketDetail({ ticketId, onChange }) {
  const [t, setT]           = useState(null);
  const [loading, setLoad]  = useState(true);
  const [err, setErr]       = useState(null);
  const [reply, setReply]   = useState('');
  const [note, setNote]     = useState('');
  const [busy, setBusy]     = useState(false);

  const load = useCallback(async () => {
    setLoad(true); setErr(null);
    try {
      const r = await adminTicketsAPI.get(ticketId);
      if (r.error) throw new Error(r.error);
      setT(r.data?.ticket || null);
    } catch (e) { setErr(e.message); }
    finally     { setLoad(false); }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={S.empty}>Loading ticket…</div>;
  if (err)     return <div style={S.empty}>{err}</div>;
  if (!t)      return null;

  // NOTE: We POST to /admin/tickets/{id}/update instead of PATCH /admin/tickets/{id}
  // because the admin API Gateway only has POST/GET configured for this resource
  // — a PATCH preflight returns no CORS headers and the browser shows
  // "Failed to fetch". The Lambda accepts both methods.
  async function updateStatus(newStatus) {
    setBusy(true);
    try {
      const r = await adminTicketsAPI.update(ticketId, { status: newStatus });
      if (r.error) throw new Error(r.error);
      await load(); onChange?.();
    } catch (e) { alert(e.message); }
    finally     { setBusy(false); }
  }
  async function updatePriority(newPriority) {
    setBusy(true);
    try {
      const r = await adminTicketsAPI.update(ticketId, { priority: newPriority });
      if (r.error) throw new Error(r.error);
      await load(); onChange?.();
    } catch (e) { alert(e.message); }
    finally     { setBusy(false); }
  }
  async function sendReply() {
    const text = reply.trim();
    if (!text) return;
    setBusy(true);
    try {
      const r = await adminTicketsAPI.addMessage(ticketId, { text, adminName: 'HCEN Support' });
      if (r.error) throw new Error(r.error);
      setReply(''); await load(); onChange?.();
    } catch (e) { alert(e.message); }
    finally     { setBusy(false); }
  }
  async function addNote() {
    const text = note.trim();
    if (!text) return;
    setBusy(true);
    try {
      const r = await adminTicketsAPI.addNote(ticketId, { text, adminName: 'Admin' });
      if (r.error) throw new Error(r.error);
      setNote(''); await load();
    } catch (e) { alert(e.message); }
    finally     { setBusy(false); }
  }

  const c = CATEGORY[t.category] || CATEGORY.other;
  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 22 }}>{c.emoji}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#f9fafb' }}>{t.subject}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
              <span style={{ fontFamily: 'monospace', color: '#fbbf24', fontWeight: 700 }}>{t.ticketId}</span>
              {' · '}{t.userName} · {t.userPhone}
              {t.bookingId && <span> · Booking: <span style={{ fontFamily: 'monospace' }}>{t.bookingId}</span></span>}
            </div>
          </div>
        </div>

        {/* Status + Priority editors */}
        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={S.miniLabel}>Status</div>
            <select disabled={busy} value={t.status}
              onChange={e => updateStatus(e.target.value)}
              style={S.editor}>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <div style={S.miniLabel}>Priority</div>
            <select disabled={busy} value={t.priority}
              onChange={e => updatePriority(e.target.value)}
              style={S.editor}>
              {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          {t.sla && (t.status === 'open' || t.status === 'in_progress') && (
            <div style={{ marginLeft: 'auto', alignSelf: 'flex-end',
                color: t.sla.isOverdue ? '#fca5a5' : '#9ca3af',
                fontSize: 12, fontWeight: 700 }}>
              {t.sla.isOverdue
                ? `🚨 ${t.sla.hoursOverdue}h overdue`
                : `⏱️ ${t.sla.hoursElapsed}h elapsed`}
            </div>
          )}
        </div>
      </div>

      {/* Thread */}
      <div style={S.thread}>
        {t.messages?.map(m => (
          <div key={m.id} style={{
            display: 'flex',
            justifyContent: m.sender === 'admin' ? 'flex-start' : 'flex-end',
            marginBottom: 10,
          }}>
            <div style={{
              maxWidth: '75%',
              background: m.sender === 'admin' ? '#1e3a5f' : '#1f2937',
              borderLeft: m.sender === 'admin' ? '3px solid #ef4444' : 'none',
              padding: '10px 12px',
              borderRadius: 10,
            }}>
              <div style={{ fontSize: 11, color: m.sender === 'admin' ? '#fca5a5' : '#9ca3af',
                  fontWeight: 700, marginBottom: 4 }}>
                {m.sender === 'admin' ? '🛡️ HCEN Support' : '👤 ' + (m.senderName || 'User')}
                <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>
                  · {fmtTime(m.createdAt)}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#e5e7eb', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {m.text}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Reply box */}
      {t.status !== 'closed' && (
        <div style={S.replyBox}>
          <textarea
            placeholder="Type a reply to the patient…"
            value={reply}
            onChange={e => setReply(e.target.value)}
            rows={3}
            style={S.textarea}
          />
          <button disabled={busy || !reply.trim()} onClick={sendReply} style={S.primaryBtn}>
            {busy ? 'Sending…' : 'Send Reply'}
          </button>
        </div>
      )}

      {/* Internal notes */}
      <div style={{ marginTop: 18, padding: 14, background: '#1f2937', borderRadius: 10,
          border: '1px dashed #4b5563' }}>
        <div style={{ fontSize: 11.5, fontWeight: 800, color: '#fbbf24', marginBottom: 8 }}>
          🔒 INTERNAL NOTES (not visible to patient)
        </div>
        {t.internalNotes?.length > 0
          ? t.internalNotes.map(n => (
              <div key={n.id} style={{ fontSize: 12, color: '#d1d5db',
                  marginBottom: 6, padding: '6px 8px', background: '#111827',
                  borderRadius: 6 }}>
                <span style={{ color: '#6b7280' }}>{fmtTime(n.createdAt)} · {n.by}:</span> {n.text}
              </div>
            ))
          : <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>No notes yet.</div>
        }
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input
            placeholder="Add a private note…"
            value={note}
            onChange={e => setNote(e.target.value)}
            style={{ ...S.search, flex: 1, marginLeft: 0 }}
          />
          <button disabled={busy || !note.trim()} onClick={addNote}
            style={{ ...S.primaryBtn, background: '#4b5563', padding: '6px 14px' }}>
            Add
          </button>
        </div>
      </div>

      {/* User rating (if any) */}
      {t.rating && (
        <div style={{ marginTop: 14, padding: 12, background: '#3a2e1e',
            borderRadius: 10, border: '1px solid #fbbf2444' }}>
          <div style={{ fontSize: 12, color: '#fbbf24', fontWeight: 700 }}>
            ⭐ User rated this resolution: {t.rating}/5
          </div>
          {t.ratingComment && (
            <div style={{ fontSize: 12, color: '#d1d5db', marginTop: 4, fontStyle: 'italic' }}>
              "{t.ratingComment}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Small bits ──────────────────────────────────────────────────────────────
function Stat({ label, value, accent }) {
  return (
    <div style={S.statCard}>
      <div style={{ fontSize: 10.5, color: '#9ca3af', textTransform: 'uppercase',
          letterSpacing: 1, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 24, color: accent, fontWeight: 800, marginTop: 6 }}>{value}</div>
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={S.editor}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) +
      ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

// ─── Styles ─────────────────────────────────────────────────────────────────
const S = {
  page: { background: '#0f172a', minHeight: '100vh', color: '#e5e7eb', padding: 20,
    fontFamily: 'Inter, system-ui, sans-serif' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center',
    minHeight: '60vh', color: '#9ca3af' },
  headerBar: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 },
  backBtn: { background: 'transparent', border: '1px solid #374151', color: '#e5e7eb',
    padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  refreshBtn: { background: '#1e3a5f', border: 'none', color: '#93c5fd',
    padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, marginLeft: 'auto' },
  primaryBtn: { background: '#3b82f6', border: 'none', color: 'white',
    padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  title: { fontSize: 22, fontWeight: 800, color: '#f9fafb' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12, marginBottom: 16 },
  statCard: { background: '#1e293b', borderRadius: 10, padding: 14, border: '1px solid #334155' },
  filterBar: { display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  editor: { background: '#1e293b', border: '1px solid #334155', color: '#e5e7eb',
    padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  miniLabel: { fontSize: 10, color: '#9ca3af', textTransform: 'uppercase',
    letterSpacing: 0.8, marginBottom: 4, fontWeight: 700 },
  search: { background: '#1e293b', border: '1px solid #334155', color: '#e5e7eb',
    padding: '7px 12px', borderRadius: 8, fontSize: 12, flex: 1, minWidth: 220, marginLeft: 'auto' },
  twoPane: { display: 'grid', gridTemplateColumns: '380px 1fr', gap: 16, alignItems: 'flex-start' },
  // NOTE: twoPane responsiveness handled via @media in index.css (.resp-grid-sidebar-left)
  listPane: { background: '#1e293b', borderRadius: 12, padding: 8, maxHeight: 'calc(100vh - 280px)',
    overflowY: 'auto', border: '1px solid #334155' },
  detailPane: { background: '#1e293b', borderRadius: 12, padding: 18,
    minHeight: 400, border: '1px solid #334155' },
  row: { padding: '10px 12px', borderRadius: 8, marginBottom: 6, cursor: 'pointer' },
  empty: { padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13 },
  thread: { maxHeight: 380, overflowY: 'auto', padding: 10, background: '#0f172a',
    borderRadius: 10, marginBottom: 12, border: '1px solid #334155' },
  replyBox: { display: 'flex', flexDirection: 'column', gap: 8 },
  textarea: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e5e7eb',
    border: '1px solid #334155', borderRadius: 8, padding: 10, fontSize: 13,
    fontFamily: 'Inter, system-ui, sans-serif', resize: 'vertical' },
  errCard: { background: '#1f2937', borderRadius: 12, padding: 24, border: '1px solid #b91c1c' },
};
